import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { DESPRE, ASSETS } from "@/data/content";

function TextBody({ body }) {
  return (
    <div className="mt-6 space-y-4">
      {body.map((p, i) => (
        <p key={i} className="text-slate-600 leading-relaxed text-[17px]">
          {p}
        </p>
      ))}
    </div>
  );
}

function LogoCard({ logo, tint }) {
  return (
    <div
      className={
        "flex items-center justify-center rounded-3xl border border-slate-100 p-12 md:p-16 shadow-[0_24px_50px_-30px_rgba(15,23,42,0.2)] " +
        (tint || "bg-white")
      }
    >
      <img src={logo} alt="" className="max-h-24 md:max-h-28 w-auto max-w-[85%] object-contain" />
    </div>
  );
}

function InkPanel({ label, tag }) {
  return (
    <div className="relative overflow-hidden rounded-3xl p-10 md:p-12 bg-gradient-to-br from-slate-800 to-slate-900">
      <div className="pointer-events-none absolute -top-10 -right-10 h-48 w-48 rounded-full bg-white/15 blur-2xl" />
      <span className="inline-block rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">
        {tag}
      </span>
      <div className="mt-10 flex items-center">
        <span className="font-display text-4xl font-extrabold text-white">{label}</span>
      </div>
    </div>
  );
}

export default function Despre() {
  return (
    <div data-testid="despre-page">
      <PageHero kicker={DESPRE.kicker} title={DESPRE.title} intro={DESPRE.intro} variant="cool" />

      {/* STATS */}
      <section className="bg-white border-b border-slate-100">
        <div className="container-x grid grid-cols-2 gap-y-8 py-12 md:grid-cols-4 md:divide-x md:divide-slate-100">
          {DESPRE.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.07} className="md:px-8 md:first:pl-0">
              <div className="text-3xl md:text-4xl font-extrabold tracking-tight text-brand">
                {s.value}
              </div>
              <div className="mt-2 text-sm text-slate-500 font-medium">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ISIC */}
      <section className="py-14 md:py-20">
        <div className="container-x grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              {DESPRE.isic.title}
            </h2>
            <TextBody body={DESPRE.isic.body} />
          </Reveal>
          <Reveal delay={0.12}>
            <LogoCard logo={ASSETS.isicColor} tint="bg-gradient-to-br from-teal-50 to-white" />
          </Reveal>
        </div>
      </section>

      {/* EYCA */}
      <section className="bg-white py-14 md:py-20 border-y border-slate-100">
        <div className="container-x grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
          <Reveal className="order-2 lg:order-1">
            <LogoCard logo={ASSETS.eycaColor} tint="bg-gradient-to-br from-pink-50 to-white" />
          </Reveal>
          <Reveal delay={0.12} className="order-1 lg:order-2">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              {DESPRE.eyca.title}
            </h2>
            <TextBody body={DESPRE.eyca.body} />
          </Reveal>
        </div>
      </section>

      {/* ISIC JOBS */}
      <section className="py-14 md:py-20">
        <div className="container-x grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              {DESPRE.jobs.title}
            </h2>
            <TextBody body={DESPRE.jobs.body} />
          </Reveal>
          <Reveal delay={0.12}>
            <InkPanel label="ISIC Jobs" tag="Employer branding & recrutare" />
          </Reveal>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-white py-14 md:py-20 border-t border-slate-100">
        <div className="container-x">
          <Reveal>
            <span className="overline">De ce noi</span>
            <h2 className="mt-5 text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {DESPRE.why.title}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {DESPRE.why.items.map((it, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div className="card h-full p-7 md:p-8">
                  <div className="mb-3 h-1.5 w-10 rounded-full bg-brand" />
                  <h3 className="text-xl font-bold tracking-tight">{it.h}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-[16px]">{it.t}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/ce-oferim" data-testid="despre-cta" className="btn-primary mt-12">
              {DESPRE.why.cta}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
